/**
 * <variant-picker> - option-baseret variant-valg paa Section Rendering (THEME-SPEC 3,
 * BUILD_PLAN 2.2). Opgraderer product-forms no-JS <select name='id'>: tilgaengelige
 * chip-radioer driver selecten (som forbliver add-to-cart id-kilde + no-JS fallback).
 *
 * Penge/locale server-ejet: ved variant-skift re-renderes main-product server-side
 * (?variant=&section_id=), og KUN pris-noden + add-knappen byttes ind. Intet pris
 * beregnes client-side - pickeren laeser kun variant-IDENTITET (id, option-kombi,
 * tilgaengelighed, featured-media) fra selectens data-attributter, aldrig priser.
 *
 * Galleri skifter client-side (scroll til variantens featured-media-slide). URL'ens
 * ?variant= opdateres via History API (ingen reload) - kun naar pickeren staar paa
 * sit eget produkts side, ikke i quick-view (data-render-url). debounce + abortable fra den
 * delte util: hurtige klik giver ét, seneste-vinder kald.
 */
import { fetchSection, debounce, abortable, latestBusy } from '@theme/section-render';

const SECTION = '[id^="shopify-section-"]';

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('[data-variant-select]');
    if (!this.select) return;
    this.live = this.querySelector('[data-variant-live]');
    this.nextSignal = abortable();
    // CUSTOM: r02-m2 - kun det seneste kald rydder busy (aria-busy staar til seneste svar lander).
    this.markBusy = latestBusy((busy) => this.toggleAttribute('aria-busy', busy));
    this.render = debounce(this.#render.bind(this), 200);
    // To-trins stoerrelsesvalg (bukser: "livvidde / laengde"). Matricen udledes
    // af SELECTEN, ikke af ny markup: hver <option> baerer data-options="30 / 32"
    // og er disabled naar varianten er udsolgt. Dermed er der én kilde til
    // tilgaengelighed. En manglende kombination (huller i ERP-data) har ingen
    // noegle i matricen og disables; en udsolgt har noeglen med false og er
    // valgbar (Klaviyo back in stock).
    this.sizeSplit = Boolean(this.querySelector('[data-size-group="length"]'));
    if (this.sizeSplit) this.#buildSizeMatrix();
    this.addEventListener('change', this.#onChange);
    // Catch-up: chipsene er synlige fra first paint (fallback-selecten er gated
    // bag html.js, ikke :defined - ellers glimtede selecten foer chipsene), saa
    // et klik kan lande FOER upgraden. Det klik naaede ingen listener, og uden
    // det her ville selecten - som ER add-to-cart's id - beholde den gamle
    // variant, mens chippen viste den nye. Er de to uenige, koeres skiftet som
    // var det lige sket. No-op i normaltilfaeldet; springes over naar INGEN
    // radio er valgt (udsolgt produkt uden selected_value), hvor uenigheden er
    // server-renderet og ikke et brugervalg.
    if (this.querySelector('input[type="radio"]:checked') && this.#matchVariantId() !== this.select.value) {
      this.#apply(true);
    }
  }

  /**
   * Offentlig indgang for komponenter UDEN for pickeren (i dag sticky-baren's
   * stoerrelsesliste): vaelg en variant paa id. Radioerne saettes ud fra
   * selectens egen option-kombi (data-options), og skiftet koerer derefter ad
   * PRAECIS samme vej som et klik - select, URL, galleri, server-render,
   * variant:change. Returnerer false ved ukendt eller udsolgt variant, og
   * hvis skiftet ikke landede paa den oenskede variant.
   *
   * scroll: false springer galleri-scrollet over. Sticky-baren kalder langt
   * nede paa siden, hvor et galleri-skift alligevel ikke ses.
   * @param {string|number} id
   * @param {{ scroll?: boolean }} [opts]
   * @returns {boolean}
   */
  selectVariant(id, { scroll = true } = {}) {
    if (!this.select) return false;
    const option = this.select.querySelector(`option[value="${CSS.escape(String(id))}"]`);
    if (!option || option.disabled) return false;
    const check = (selector, value) => {
      const radio = [...this.querySelectorAll(selector)].find((r) => r.value === value);
      if (radio) radio.checked = true;
    };
    if (this.sizeSplit) {
      const parts = (option.dataset.options || '').split('/');
      if (parts.length !== 2) return false;
      check('input[data-size-part="waist"]', parts[0].trim());
      check('input[data-size-part="length"]', parts[1].trim());
    } else {
      (option.dataset.options || '').split('~~').forEach((value, index) => {
        check(`input[data-option-position="${index + 1}"]`, value);
      });
    }
    this.#apply(this.sizeSplit, { scroll });
    // Landede skiftet ikke paa den oenskede variant (radio-vaerdier og
    // data-options uenige, fx efter et ERP-formatskift), siges det.
    return this.select.value === String(id);
  }

  #buildSizeMatrix() {
    this.sizeMatrix = new Map();
    [...this.select.options].forEach((opt) => {
      const parts = (opt.dataset.options || '').split('/');
      if (parts.length !== 2) return;
      this.sizeMatrix.set(`${parts[0].trim()}|${parts[1].trim()}`, !opt.disabled);
    });
  }

  #sizePart(part) {
    const radio = this.querySelector(`input[data-size-part="${part}"]:checked`);
    return radio ? radio.value : '';
  }

  // Efter et livvidde-valg: laengderne viser DEN livviddes lager. Uden valgt
  // livvidde vises global tilgaengelighed (en laengde streges kun ud hvis ingen
  // livvidde har den). Udsolgt er valgbart (data-soldout) - kun en valgt udsolgt
  // variant viser Klaviyo back in stock. Kun en kombi der slet ikke FINDES
  // disables; er den valgte laengde en saadan, ryddes den - aldrig et lydloest
  // skift til en anden laengde, for laengde er et pasformsvalg.
  #syncLengths() {
    const waist = this.#sizePart('waist');
    let clearedValue = '';
    this.querySelectorAll('input[data-size-part="length"]').forEach((radio) => {
      const suffix = `|${radio.value}`;
      const exists = waist
        ? this.sizeMatrix.has(`${waist}${suffix}`)
        : [...this.sizeMatrix.keys()].some((key) => key.endsWith(suffix));
      const available = waist
        ? this.sizeMatrix.get(`${waist}${suffix}`) === true
        : [...this.sizeMatrix].some(([key, ok]) => ok && key.endsWith(suffix));
      radio.disabled = !exists;
      radio.toggleAttribute('data-soldout', exists && !available);
      const label = this.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      if (label) {
        const note = label.querySelector('[data-size-soldout]');
        if (available && note) note.remove();
        if (!available && !note) {
          const span = document.createElement('span');
          span.className = 'visually-hidden';
          span.dataset.sizeSoldout = '';
          span.textContent = ` - ${this.dataset.textSoldOut || ''}`;
          label.appendChild(span);
        }
      }
      if (!exists && radio.checked) {
        radio.checked = false;
        clearedValue = radio.value;
      }
    });
    return clearedValue;
  }

  // Ingen komplet kombination -> knappen deaktiveres med "Vaelg stoerrelse".
  // Naar begge trin er valgt, kommer knappens rigtige tilstand fra serverens
  // re-render (den ejer ogsaa udsolgt/pris), saa vi roerer den ikke der.
  #setIncomplete() {
    const button =
      this.querySelector('[data-add-button] button, [data-add-button] .btn') ||
      this.closest('form')?.querySelector('[data-add-button] button');
    if (!button) return;
    button.disabled = true;
    button.textContent = this.dataset.textChooseSize || '';
  }

  #onChange = (event) => {
    if (!event.target.matches('input[type="radio"]')) return;
    this.#apply(this.sizeSplit && event.target.dataset.sizePart === 'waist');
  };

  // Faelles vej for et brugerskift og for catch-up'en ved upgrade.
  #apply(fromWaist, { scroll = true } = {}) {
    if (this.sizeSplit) {
      if (fromWaist) {
        const cleared = this.#syncLengths();
        if (cleared && this.live) this.live.textContent = '';
      }
      // Kun én halvdel valgt: der findes ingen variant at rendere endnu.
      if (!this.#sizePart('waist') || !this.#sizePart('length')) {
        this.#setIncomplete();
        this.#broadcast(null);
        return;
      }
    }
    const id = this.#matchVariantId();
    if (!id) {
      this.#broadcast(null);
      return;
    }
    // Straks (client-side, ingen penge): select-vaerdi = add-to-cart id, URL, galleri.
    this.select.value = id;
    // ?variant= er kun sidens egen URL naar pickeren staar paa sit eget
    // produkts side. Med data-render-url lever den i quick-view paa en ANDEN
    // side (PLP-promo eller PDP'ens complementary-blok), og variant-id'et
    // ville pege sidens URL paa et fremmed produkt.
    if (!this.dataset.renderUrl) this.#updateUrl(id);
    if (scroll) this.#scrollGallery(id);
    // Pris + knap-tilstand: server-renderet (debounced).
    this.render(id);
    this.#broadcast(id);
  }

  // Broadcast til komponenter der skal FOELGE varianten uden at kende
  // pickerens indmad - <product-journey> (Retraced-SKU) og <sticky-buy-bar>.
  // Bobler, saa lytteren kan staa paa document;
  // lytteren maa selv frasortere en picker med data-render-url (quick-view
  // staar paa en ANDEN vare). Ingen penge i detail: pris er og bliver
  // serverens.
  //
  // id KAN vaere null, og det er en del af kontrakten: der er ikke laengere en
  // hel variant valgt (halvt bukse-valg, eller en kombi ERP ikke har). En
  // lytter der viser noget variant-specifikt skal kunne TRAEKKE DET TILBAGE -
  // ellers stod noget fremme, bundet til den forrige variant, mens pickeren
  // viser "vaelg stoerrelse". Foer dette fyrede de to
  // veje slet intet event. De oevrige lyttere frasorterer et falsy id i
  // forvejen (sticky-buy-bar.js, product-journey.js), saa deres adfaerd er
  // uaendret.
  #broadcast(id) {
    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { id } }));
  }

  // Saml valgte option-vaerdier pr. position, og find varianten med samme kombi.
  #matchVariantId() {
    if (this.sizeSplit) {
      // Kombinationen laeses IKKE ved at bygge strengen "W / L" selv - separator
      // og mellemrum kommer fra ERP. Vi sammenligner de to dele hver for sig
      // mod selectens egen opdeling, saa formatet aldrig kan drive fra hinanden.
      const waist = this.#sizePart('waist');
      const length = this.#sizePart('length');
      if (!waist || !length) return null;
      const option = [...this.select.options].find((opt) => {
        const parts = (opt.dataset.options || '').split('/');
        return parts.length === 2 && parts[0].trim() === waist && parts[1].trim() === length;
      });
      return option ? option.value : null;
    }
    const chosen = [];
    this.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
      chosen[Number(radio.dataset.optionPosition)] = radio.value;
    });
    const combo = chosen.filter((value) => value != null).join('~~');
    const option = [...this.select.options].find((opt) => opt.dataset.options === combo);
    return option ? option.value : null;
  }

  #updateUrl(id) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState({}, '', url);
  }

  #scrollGallery(id) {
    const option = this.select.querySelector(`option[value="${CSS.escape(id)}"]`);
    const mediaId = option && option.dataset.featuredMedia;
    if (!mediaId) return;
    const slide = document.getElementById(`product-media-${mediaId}`);
    // Kun sporet scrolles, vandret - aldrig siden. scrollIntoView scroller ALLE
    // forfaedre: paa mobil staar galleriet over stoerrelsesvalget, og
    // block: 'nearest' kastede kunden op til toppen ved hvert chip-klik. Ligger
    // slidet uden for sporet (understregionen), roeres intet.
    const track = slide && slide.closest('[data-gallery-track]');
    if (!track) return;
    const left = slide.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
    // reduced motion: hop i stedet for smooth (samme gate som collection-filters).
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left, behavior: reduce ? 'auto' : 'smooth' });
  }

  async #render(id) {
    const section = this.closest(SECTION);
    if (!section) return;
    const sectionId = section.id.replace('shopify-section-', '');
    // PDP re-renderer paa den aktuelle URL (product-template). I quick-view (PLP-
    // promo eller PDP'ens complementary-blok) saetter quick-view-product
    // data-render-url=produkt-URL, saa serveren har produktets side-kontekst og
    // ?variant= vaelger den rigtige variant (pris server-ejet).
    const url = this.dataset.renderUrl || undefined;
    const doneBusy = this.markBusy(); // CUSTOM: r02-m2
    try {
      const html = await fetchSection(sectionId, { url: url, params: { variant: id }, signal: this.nextSignal() });
      const doc = new DOMParser().parseFromString(html, 'text/html');
      this.#swap(section, doc, '[data-product-price]');
      this.#swap(section, doc, '[data-product-scarcity]');
      this.#swapButton(section, doc);
      this.#announce(section.querySelector('[data-product-price]'));
      this.#clearError();
    } catch (error) {
      // Afbrudt af et nyere valg er ikke en fejl. Ved andre fejl beholder pris/knap
      // deres forrige (gyldige) tilstand - men det siges HOEJT (role=alert-boks +
      // live-region), for URL + galleri er allerede skiftet.
      if (error.name === 'AbortError') return;
      this.#showError();
    } finally {
      doneBusy(); // CUSTOM: r02-m2
    }
  }

  // Synlig + hoerbar fejl ved fejlet variant-render. Teksten er server-
  // lokaliseret (data-text-error paa hosten); boksen er role=alert (annoncerer
  // selv), saa den polite live-region roeres ikke (ingen dobbelt-oplaesning).
  #showError() {
    const box = this.querySelector('[data-variant-error]');
    if (!box) return;
    // Unhide FOER teksten saettes: role=alert annoncerer indsat tekst mere
    // paalideligt end en unhide af allerede-udfyldt indhold.
    box.hidden = false;
    box.textContent = this.dataset.textError || '';
  }

  #clearError() {
    const box = this.querySelector('[data-variant-error]');
    if (box) {
      box.hidden = true;
      box.textContent = '';
    }
  }

  // Byt et fragments indhold (scoped til denne section). Main-product bruger
  // {% stylesheet %} (tema-global CSS allerede paa siden), saa vi baerer IKKE nogen
  // section-style med - modsat cart/search, hvis asset-stylesheets kommer i svaret.
  #swap(scope, doc, selector) {
    const target = scope.querySelector(selector);
    const source = doc.querySelector(selector);
    if (target && source) target.replaceChildren(...source.childNodes);
  }

  // Add-knappen byttes som ELEMENT, ikke hele [data-add-button]'s indhold.
  // Klaviyo back in stock indsaetter sin "Notify me"-knap som soeskende til
  // add-knappen, INDE i wrapperen (maalt 2026-09-28 paa kca-eur-eng). Et replaceChildren fjernede
  // den straks efter Klaviyo havde vist den. Klaviyo viser/skjuler selv sin
  // knap ved variant-skift, saa vi skal blot lade fremmede noder staa.
  #swapButton(scope, doc) {
    const target = scope.querySelector('[data-add-button] > .btn');
    const source = doc.querySelector('[data-add-button] > .btn');
    if (target && source) target.replaceWith(source);
  }

  // Annoncér variant + server-renderet pris til skaermlaeser (prisen LAESES fra den
  // friske node, aldrig genberegnet). Variant-titlen baerer ogsaa "udsolgt"-tilstand.
  #announce(priceEl) {
    if (!this.live) return;
    const selected = this.select.options[this.select.selectedIndex];
    const title = selected ? selected.textContent.trim().replace(/\s+/g, ' ') : '';
    const priceText = priceEl ? priceEl.textContent.trim().replace(/\s+/g, ' ') : '';
    const message = priceText ? `${title}. ${priceText}` : title;
    this.live.textContent = '';
    requestAnimationFrame(() => {
      this.live.textContent = message;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
